import { useVoucherMap } from '../lib/useVoucherMap';
import { logger } from '../lib/logger';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShieldCheck, Zap, Download, BadgeCheck, ArrowRight, ArrowUpRight } from 'lucide-react';
import { api } from '../lib/api';
import { useSeo, seoUrl } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { CategoryIcon } from '../components/Icons';

const SELLER_IMG = 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const ENGINEER_IMG = 'https://images.unsplash.com/photo-1659353587228-5dbbebb5d98f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800';

// Cache module-level untuk shared promise early-fetch: tahan double-mount StrictMode (dev)
// tanpa request duplikat; auto-clear setelah settle agar kunjungan berikutnya fetch fresh.
let homeEarlyPromise;

export default function Home() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useSeo({
    description: 'TokoSipil.id — Marketplace produk digital konstruksi Indonesia: gambar kerja, RAB, template Excel, dokumen tender & proyek dari seller terverifikasi.',
    canonical: seoUrl('/'),
    jsonLd: [
      { '@context': 'https://schema.org', '@type': 'Organization', name: 'TokoSipil.id', url: seoUrl('/') },
      { '@context': 'https://schema.org', '@type': 'WebSite', name: 'TokoSipil.id', url: seoUrl('/'),
        potentialAction: { '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${seoUrl('/produk')}?search={search_term_string}` },
          'query-input': 'required name=search_term_string' } },
    ],
  });
  const navigate = useNavigate();

  // SATU request voucher per halaman: gabungkan semua section (dedupe product_id),
  // lalu voucherMap dioper ke tiap ProductGrid via prop (anti duplikat fetch per-section).
  const allProducts = [];
  const seenIds = new Set();
  for (const p of [...(data?.bestsellers || []), ...(data?.official || []), ...(data?.latest || []), ...(data?.recommended || [])]) {
    if (!seenIds.has(p.product_id)) { seenIds.add(p.product_id); allProducts.push(p); }
  }
  const voucherMap = useVoucherMap(allProducts);

  useEffect(() => {
    const applyData = (d) => setData(d);
    const onError = (e) => {
      logger.warn('Gagal memuat beranda:', e?.message);
      setData({ latest: [], bestsellers: [], recommended: [], official: [], articles: [], categories: [] });
    };
    // Early-fetch dari index.html: konsumsi shared promise via cache module-level
    // (StrictMode double-mount tidak memicu request kedua). Fallback ke axios bila gagal/absen.
    if (homeEarlyPromise === undefined && window.__TS_HOME_PROMISE__) {
      homeEarlyPromise = window.__TS_HOME_PROMISE__;
      delete window.__TS_HOME_PROMISE__;
      const clear = () => { homeEarlyPromise = null; };
      homeEarlyPromise.then(clear, clear);
    }
    if (homeEarlyPromise) {
      homeEarlyPromise.then(applyData).catch(() => api.get('/home').then((r) => applyData(r.data)).catch(onError));
    } else {
      api.get('/home').then((r) => applyData(r.data)).catch(onError);
    }
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/produk?search=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <Header />

      {/* HERO */}
      <section className="relative hero-texture noise-overlay overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-20">
          {/* Hero LCP: self-hosted WebP (bukan Unsplash eksternal), eager + fetchpriority tinggi,
              dimensi eksplisit (anti-CLS), preload dari index.html — visual identik (opacity-20 bg). */}
          <img src="/hero-construction-1600.webp" srcSet="/hero-construction-800.webp 800w, /hero-construction-1600.webp 1600w" sizes="100vw"
            alt="" width="1600" height="1295" loading="eager" fetchPriority="high" decoding="async"
            className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/60 to-brand-black/40" />
        {/* Visual blueprint kanan — static asset, hanya desktop, tidak mengganggu heading */}
        <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[38%] pointer-events-none" aria-hidden="true">
          <img src="/hero-blueprint.webp" alt="" width="1600" height="1067" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-40 [mask-image:linear-gradient(to_left,black_55%,transparent)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl lg:max-w-[56rem]">
            <p className="text-brand-red text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-6 animate-fade-in">Marketplace Produk Digital Konstruksi</p>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight !leading-[1.15] animate-fade-in">
              Gambar Kerja, RAB & Dokumen Teknis.
              <span className="text-brand-red"> Siap Pakai.</span>
            </h1>
            <p className="text-zinc-300 text-base sm:text-lg mt-6 leading-relaxed max-w-2xl animate-fade-in">
              TokoSipil.id adalah marketplace produk digital untuk kebutuhan konstruksi, arsitektur, dan teknik sipil.
              Unduh file dari para praktisi — mulai dari gambar AutoCAD hingga template RAB Excel.
            </p>
            <form onSubmit={doSearch} className="mt-10 flex max-w-2xl animate-fade-in" data-testid="hero-search-form">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" strokeWidth={1.5} />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari gambar kerja, RAB, template, dokumen tender..."
                  className="w-full h-14 pl-12 pr-4 bg-white text-brand-black text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-brand-red"
                  data-testid="hero-search-input"
                />
              </div>
              <button type="submit" className="h-14 px-6 sm:px-8 bg-brand-red text-white font-semibold text-sm hover:bg-brand-redhover transition-colors active:scale-95" data-testid="hero-search-btn">
                Cari
              </button>
            </form>
            <div className="flex flex-wrap gap-x-6 gap-y-3 mt-10 text-zinc-400 text-xs font-medium tracking-wide">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-red" strokeWidth={1.5} /> Produk Relevan Konstruksi</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-brand-red" strokeWidth={1.5} /> Pembayaran Aman</span>
              <span className="flex items-center gap-2"><Download className="w-4 h-4 text-brand-red" strokeWidth={1.5} /> Download Instan</span>
              <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-brand-red" strokeWidth={1.5} /> Seller &amp; Review Terverifikasi</span>
            </div>
          </div>
        </div>
      </section>

      {/* KATEGORI POPULER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="categories-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-brand-red text-xs font-semibold tracking-[0.25em] uppercase mb-2">Kategori</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">Kategori Populer</h2>
          </div>
          <Link to="/kategori" className="hidden sm:flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-brand-red transition-colors" data-testid="lihat-semua-kategori">
            Semua Kategori <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
          {(data?.categories || []).map((c, i) => (
            <Link key={c.category_id} to={`/produk?category=${c.category_id}`} data-testid={`category-card-${c.slug}`}
              className="group border border-border p-5 hover:border-brand-black hover:-translate-y-1 hover:shadow-lg transition-all duration-200 animate-fade-in"
              style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-11 h-11 bg-zinc-100 group-hover:bg-brand-red group-hover:text-white text-zinc-700 flex items-center justify-center transition-colors mb-4">
                <CategoryIcon name={c.icon} className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-bold text-sm leading-tight">{c.name}</h3>
              <p className="text-xs text-zinc-400 mt-1">{c.product_count || 0} produk</p>
            </Link>
          ))}
        </div>
      </section>

      {/* PRODUK TERLARIS */}
      <section className="bg-zinc-50 border-y border-border" data-testid="bestseller-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <SectionHeader tag="Terlaris" title="Produk Terlaris" link="/produk?sort=terlaris" linkTestid="lihat-terlaris" />
          <ProductGrid products={data?.bestsellers} voucherMap={voucherMap} />
        </div>
      </section>

      {/* OFFICIAL STORE — hanya produk dari seller berstatus Official Store (filter backend) */}
      {data && (data.official || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="official-store-section">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-brand-red text-xs font-semibold tracking-[0.25em] uppercase mb-2 flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4" strokeWidth={1.5} /> Toko Resmi Terverifikasi
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">Official Store</h2>
            </div>
          </div>
          <ProductGrid products={data.official} voucherMap={voucherMap} />
        </section>
      )}

      {/* PRODUK TERBARU */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="latest-section">
        <SectionHeader tag="Baru Rilis" title="Produk Terbaru" link="/produk?sort=terbaru" linkTestid="lihat-terbaru" />
        <ProductGrid products={data?.latest} voucherMap={voucherMap} />
      </section>

      {/* REKOMENDASI */}
      <section className="bg-zinc-50 border-y border-border" data-testid="recommended-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <SectionHeader tag="Pilihan Editor" title="Produk Rekomendasi" link="/produk" linkTestid="lihat-rekomendasi" />
          <ProductGrid products={data?.recommended} voucherMap={voucherMap} />
        </div>
      </section>

      {/* KENAPA TOKOSIPIL - BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="why-section">
        <p className="text-brand-red text-xs font-semibold tracking-[0.25em] uppercase mb-2">Kenapa TokoSipil?</p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-12">Dibangun Khusus untuk Industri Konstruksi</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 relative overflow-hidden bg-brand-black text-white p-10 noise-overlay min-h-[320px] flex flex-col justify-end">
            <img src={ENGINEER_IMG} alt="Engineer" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative">
              <ShieldCheck className="w-8 h-8 text-brand-red mb-4" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-2xl tracking-tight">Kurasi Ketat oleh Admin</h3>
              <p className="text-zinc-300 text-sm mt-2 max-w-md leading-relaxed">Setiap produk direview sebelum tayang. Tidak ada file asal-asalan — hanya dokumen teknis yang layak pakai di proyek nyata.</p>
            </div>
          </div>
          <div className="md:col-span-5 border border-border p-10 flex flex-col justify-between hover:border-brand-black transition-colors">
            <Zap className="w-8 h-8 text-brand-red" strokeWidth={1.5} />
            <div className="mt-8">
              <h3 className="font-heading font-bold text-xl tracking-tight">Unduh Instan, Selamanya</h3>
              <p className="text-zinc-500 text-sm mt-2 leading-relaxed">Sekali beli, file tersimpan di library akun Anda. Unduh kapan saja, di mana saja.</p>
            </div>
          </div>
          <div className="md:col-span-5 border border-border p-10 flex flex-col justify-between hover:border-brand-black transition-colors">
            <BadgeCheck className="w-8 h-8 text-brand-red" strokeWidth={1.5} />
            <div className="mt-8">
              <h3 className="font-heading font-bold text-xl tracking-tight">Seller adalah Praktisi</h3>
              <p className="text-zinc-500 text-sm mt-2 leading-relaxed">Produk dibuat oleh konsultan, kontraktor, drafter, dan engineer berpengalaman — bukan penjual abal-abal.</p>
            </div>
          </div>
          <div className="md:col-span-7 bg-zinc-100 p-10 flex flex-col justify-between noise-overlay relative overflow-hidden">
            <div className="relative">
              <Download className="w-8 h-8 text-brand-red" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-xl tracking-tight mt-8">Format Lengkap: DWG, XLSX, PDF, SKP, RVT</h3>
              <p className="text-zinc-500 text-sm mt-2 max-w-lg leading-relaxed">Dari gambar kerja AutoCAD, template Excel, dokumen Word, model SketchUp, hingga file BIM Revit — semua format yang biasa Anda pakai di proyek.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ARTIKEL TERBARU */}
      <section className="bg-zinc-50 border-y border-border" data-testid="articles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <SectionHeader tag="Insight" title="Artikel Terbaru" link="/artikel" linkTestid="lihat-artikel" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(data?.articles || []).map((a) => (
              <Link key={a.article_id} to={`/artikel/${a.slug}`} data-testid={`article-card-${a.slug}`}
                className="group bg-white border border-border hover:border-brand-black hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
                <div className="aspect-video overflow-hidden bg-zinc-100">
                  {a.thumbnail ? (
                    <img src={a.thumbnail} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full bg-[repeating-linear-gradient(45deg,#f4f4f5_0_12px,#fafafa_12px_24px)]" data-testid={`article-thumb-fallback-${a.slug}`} />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg leading-snug group-hover:text-brand-red transition-colors">{a.title}</h3>
                  <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{a.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-red mt-4">
                    Baca Artikel <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SELLER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="cta-seller-section">
        <div className="relative overflow-hidden bg-brand-black text-white noise-overlay">
          <img src={SELLER_IMG} alt="Arsitek bekerja" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="relative p-10 sm:p-16 lg:p-24 max-w-2xl">
            <p className="text-brand-red text-xs font-semibold tracking-[0.3em] uppercase mb-4">Untuk Konsultan, Drafter & Engineer</p>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none">
              Ubah File Proyek Anda Menjadi Penghasilan
            </h2>
            <p className="text-zinc-300 text-base mt-6 leading-relaxed">
              Punya template RAB, gambar kerja, atau dokumen teknis yang sering dipakai ulang? Jual di TokoSipil.id dan raih penghasilan pasif dari ribuan profesional konstruksi.
            </p>
            <Link to="/jadi-seller" data-testid="cta-jadi-seller-btn"
              className="inline-flex items-center gap-2 mt-8 bg-brand-red text-white font-semibold text-sm px-8 py-4 hover:bg-brand-redhover transition-colors active:scale-95">
              Mulai Jualan Sekarang <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SectionHeader({ tag, title, link, linkTestid }) {
  return (
    <div className="flex items-end justify-between mb-10">
      <div>
        <p className="text-brand-red text-xs font-semibold tracking-[0.25em] uppercase mb-2">{tag}</p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">{title}</h2>
      </div>
      <Link to={link} className="hidden sm:flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-brand-red transition-colors" data-testid={linkTestid}>
        Lihat Semua <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </Link>
    </div>
  );
}

function ProductGrid({ products, voucherMap }) {
  if (!products) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => <div key={i} className="border border-border bg-white aspect-[3/4] animate-pulse" />)}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {products.map((p, i) => (
        <div key={p.product_id} className="animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
          <ProductCard product={p} hasVoucher={voucherMap.has(p.product_id)} />
        </div>
      ))}
    </div>
  );
}
